import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { FlaskConical, Trophy, ShieldCheck, AlertTriangle, CheckCircle2 } from "lucide-react";
import { DonkeyMascot } from "@/components/DonkeyMascot";

const VERDICT_STYLE = {
  READY_TO_SCALE: "text-donkey-green border-donkey-green/50",
  READY_FOR_CONTROLLED_GROWTH: "text-donkey-green border-donkey-green/40",
  MORE_EVIDENCE_NEEDED: "text-donkey-amber border-donkey-amber/40",
  NOT_READY_TO_GROW: "text-donkey-amber border-donkey-amber/40",
  PAUSE_AND_FIX_FOUNDATIONS: "text-donkey-red border-donkey-red/40",
  PIVOT_THE_GROWTH_APPROACH: "text-donkey-red border-donkey-red/40",
  STOP_INVESTING_IN_THIS_APPROACH: "text-donkey-red border-donkey-red/50",
};
const FIELDS = [["objective", "Growth objective"], ["hypothesis", "Measurable hypothesis"], ["segment", "Target segment"],
  ["channel", "Channel"], ["intervention", "Proposed intervention"], ["baseline", "Baseline"], ["baseline_source", "Baseline source"],
  ["primary_metric", "Primary success metric"], ["guardrail_metrics", "Guardrail metrics"], ["success_criteria", "Success criteria"],
  ["failure_criteria", "Failure criteria"], ["stop_loss", "Stop-loss"], ["capacity_constraint", "Capacity constraint"], ["owner_name", "Responsible owner"]];

export default function GrowthExperiments() {
  const { bid } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("verdict");
  const [exps, setExps] = useState([]);
  const [perm, setPerm] = useState({ can_edit: false, can_approve: false });
  const [verdict, setVerdict] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ budget_limit_gbp: 0, time_limit_days: 14 });

  const load = useCallback(async () => {
    try {
      const [e, v] = await Promise.all([api.get(`/growth/${bid}/experiments`), api.get(`/growth/${bid}/verdict`)]);
      setExps(e.data.experiments); setPerm({ can_edit: e.data.can_edit, can_approve: e.data.can_approve }); setVerdict(v.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); if (err.response?.status === 402) navigate("/dashboard"); }
  }, [bid, navigate]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => { try { await fn(); if (ok) toast.success(ok); await load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const create = () => act(() => api.post(`/growth/${bid}/experiments`, form).then(() => { setCreating(false); setForm({ budget_limit_gbp: 0, time_limit_days: 14 }); }), "Experiment created");
  const markReady = (id) => act(() => api.post(`/growth/${bid}/experiments/${id}/status`, { status: "ready" }), "Marked ready");
  const decide = (id) => act(() => api.post(`/growth/${bid}/experiments/${id}/decision`, { confidence: "medium" }), "Decision recorded");

  if (!verdict) return <div className="min-h-[50vh]" />;

  return (
    <div className="mx-auto max-w-5xl px-5 sm:px-8 py-10 text-donkey-warm" data-testid="growth-experiments-page">
      <div className="flex items-center gap-3 mb-1"><FlaskConical className="w-7 h-7 text-donkey-orange" />
        <h1 className="font-heading font-extrabold text-2xl sm:text-3xl">Growth Experiments & Verdict</h1></div>
      <p className="text-xs text-donkey-grey mb-6">Evidence-led experiments and one honest, deterministic growth verdict. Growth is never guaranteed.</p>

      <div className="flex gap-2 border-b border-white/10 mb-6" role="tablist">
        {[["verdict", "Growth Verdict"], ["experiments", "Experiments"]].map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`gtab-${k}`}
            className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === k ? "border-donkey-orange text-donkey-warm" : "border-transparent text-donkey-grey hover:text-donkey-warm"}`}>{l}</button>
        ))}
      </div>

      {tab === "verdict" && (
        <div className="space-y-5" data-testid="growth-verdict">
          <div className="card-donkey rounded-2xl p-6 flex flex-col sm:flex-row gap-5 items-start">
            <DonkeyMascot size={96} alt="" className="shrink-0 opacity-95" />
            <div className="flex-1">
              <p className="text-xs text-donkey-grey">Donkey Growth Verdict</p>
              <p className={`inline-block mt-1 font-heading font-extrabold text-2xl border rounded-lg px-3 py-1 ${VERDICT_STYLE[verdict.verdict] || ""}`} data-testid="verdict-outcome">{verdict.verdict.replace(/_/g, " ")}</p>
              <p className="text-sm text-donkey-grey mt-3">Safest next action: <span className="text-donkey-warm">{verdict.safest_next_action}</span></p>
              <p className="text-xs text-donkey-grey mt-2">{verdict.confidence}</p>
            </div>
          </div>
          {verdict.blockers.length > 0 && (
            <div className="card-donkey rounded-2xl p-5" data-testid="verdict-blockers">
              <p className="font-heading font-semibold flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-donkey-amber" /> What's blocking growth</p>
              <ul className="space-y-1.5">{verdict.blockers.map((b, i) => <li key={i} className="text-sm text-donkey-warm/85 flex gap-2"><span className="text-donkey-amber">•</span> {b}</li>)}</ul>
            </div>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(verdict.dimensions).map(([k, v]) => (
              <div key={k} className="card-donkey rounded-xl p-3"><p className="text-xs text-donkey-grey capitalize">{k.replace(/_/g, " ")}</p><p className="text-sm text-donkey-warm mt-1">{v}</p></div>
            ))}
          </div>
          <div className="card-donkey rounded-2xl p-5" data-testid="growth-plan">
            <p className="font-heading font-semibold flex items-center gap-2 mb-3"><Trophy className="w-4 h-4 text-donkey-orange" /> 30 / 60 / 90-day growth plan</p>
            {["30", "60", "90"].map((k) => (
              <div key={k} className="mb-3">
                <p className="text-xs text-donkey-orange font-mono mb-1">{k} days</p>
                {verdict.plan[k].map((it, i) => (
                  <div key={i} className="text-sm text-donkey-warm/85 border border-white/8 rounded-lg p-3">
                    <p>{it.action}</p>
                    <p className="text-xs text-donkey-grey mt-1">Owner: {it.owner} · Evidence: {it.evidence_required} · Stop: {it.stopping_condition}</p>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <p className="text-xs text-donkey-grey" data-testid="verdict-evidence-note">{verdict.evidence_note}</p>
        </div>
      )}

      {tab === "experiments" && (
        <div className="space-y-4" data-testid="experiments-panel">
          {perm.can_edit && !creating && <Button onClick={() => setCreating(true)} className="bg-donkey-orange text-donkey-black font-semibold" data-testid="new-experiment-btn">New experiment</Button>}
          {creating && (
            <div className="card-donkey rounded-2xl p-5 space-y-3" data-testid="experiment-form">
              {FIELDS.map(([k, label]) => (
                <div key={k}><label className="text-xs text-donkey-grey">{label}</label>
                  {k === "hypothesis" || k === "intervention" ? (
                    <Textarea value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} aria-label={label} className="bg-donkey-bg border-white/15 mt-1" rows={2} data-testid={`exp-${k}`} />
                  ) : (
                    <Input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} aria-label={label} className="bg-donkey-bg border-white/15 mt-1" data-testid={`exp-${k}`} />
                  )}
                </div>
              ))}
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-donkey-grey">Budget limit £ (0 allowed)</label><Input type="number" value={form.budget_limit_gbp} onChange={(e) => setForm({ ...form, budget_limit_gbp: parseFloat(e.target.value) })} aria-label="Budget limit" className="bg-donkey-bg border-white/15 mt-1" data-testid="exp-budget" /></div>
                <div><label className="text-xs text-donkey-grey">Time limit (days)</label><Input type="number" value={form.time_limit_days} onChange={(e) => setForm({ ...form, time_limit_days: parseInt(e.target.value) })} aria-label="Time limit" className="bg-donkey-bg border-white/15 mt-1" data-testid="exp-time" /></div>
              </div>
              <div className="flex gap-2"><Button onClick={create} className="bg-donkey-orange text-donkey-black" data-testid="save-experiment-btn">Create</Button>
                <Button variant="ghost" onClick={() => setCreating(false)} className="border border-white/10">Cancel</Button></div>
            </div>
          )}
          {exps.map((e) => (
            <div key={e.id} className="card-donkey rounded-2xl p-4" data-testid={`experiment-${e.id}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium">{e.objective || "Untitled experiment"}</p>
                <span className="text-[10px] uppercase tracking-wider border rounded px-1.5 py-0.5 text-donkey-grey border-white/20">{e.status.replace(/_/g, " ")}</span>
              </div>
              <p className="text-xs text-donkey-grey mt-1">{e.hypothesis}</p>
              {e.readiness_missing?.length > 0 && e.status === "draft" && (
                <p className="text-xs text-donkey-amber mt-2" data-testid={`readiness-${e.id}`}>Not ready — missing: {e.readiness_missing.join("; ")}</p>
              )}
              {e.decision && <p className="text-xs mt-2 flex items-center gap-1.5 text-donkey-green"><CheckCircle2 className="w-3.5 h-3.5" /> Decision: {e.decision.replace(/_/g, " ")}{e.override && " (overridden)"}</p>}
              <div className="flex gap-2 mt-3">
                {perm.can_edit && e.status === "draft" && e.readiness_missing?.length === 0 && <Button size="sm" onClick={() => markReady(e.id)} className="bg-transparent border border-white/15 text-donkey-warm h-8" data-testid={`ready-${e.id}`}>Mark ready</Button>}
                {perm.can_approve && ["ready", "running", "paused"].includes(e.status) && <Button size="sm" onClick={() => decide(e.id)} className="bg-donkey-orange text-donkey-black h-8" data-testid={`decide-${e.id}`}>Complete & decide</Button>}
              </div>
            </div>
          ))}
          {exps.length === 0 && !creating && <p className="text-sm text-donkey-grey">No experiments yet.</p>}
        </div>
      )}
    </div>
  );
}
